/**
 * PreviewStep — the "fixed data" table.
 *
 * Renders the SERVER's normalized values (the exact bytes execute will
 * write), never the raw file: coerced amounts, lowercased emails,
 * resolved stages and links. Cells with issues are highlighted
 * (amber = imported with a fix, red = row blocked); duplicate rows say
 * what they matched and what will happen to them. The Import button is
 * gated on zero errors — warnings are allowed and say so.
 *
 * Every row is clickable: RowDetailDialog shows the original file data
 * (headers over values) and lets the user edit the row, pull a name
 * from the email, or drop the row — no CSV round-trip. Edits and drops
 * re-run the server preview so the table never lies.
 */

import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  FiasLeadsImportDedupStrategy,
  FiasLeadsImportNameFallback,
  FiasLeadsImportPreviewResponse,
  FiasLeadsImportPreviewRow,
} from '@/lib/api';
import type { ImportFieldDescriptor, ImportMapping } from '../../lib/csv-mapping';
import { RowDetailDialog } from './RowDetailDialog';

const PREVIEW_ROW_RENDER_CAP = 50;

type RowFilter = 'all' | 'ok' | 'warning' | 'error' | 'duplicate';

interface Props {
  preview: FiasLeadsImportPreviewResponse;
  fields: ReadonlyArray<ImportFieldDescriptor>;
  mapping: ImportMapping;
  dedupStrategy: FiasLeadsImportDedupStrategy;
  onDedupStrategyChange: (strategy: FiasLeadsImportDedupStrategy) => void;
  nameFallback: FiasLeadsImportNameFallback;
  onNameFallbackChange: (fallback: FiasLeadsImportNameFallback) => void;
  /** Rows exactly as parsed from the file, by original row index. */
  csvRows: Array<Record<string, string>>;
  csvHeaders: string[];
  /** Current target-shaped values (mapping + edits), by original index. */
  effectiveRows: Array<Record<string, string>>;
  droppedRows: number[];
  onApplyRowEdit: (index: number, values: Record<string, string>) => void;
  onDropRow: (index: number) => void;
  onRestoreRow: (index: number) => void;
}

/** "First name is required; Notes: too long" — the visible reason(s) a
 * row is blocked, built from its error-level field issues. */
function blockedReasons(row: FiasLeadsImportPreviewRow, labelByField: Map<string, string>): string {
  return Object.entries(row.fields)
    .filter(([, cell]) => cell.issue?.level === 'error')
    .map(([field, cell]) => {
      const message = cell.issue!.message;
      const label = labelByField.get(field) ?? field;
      // Skip the label when the message already names the field
      // ("First name is required" needs no "First name:" prefix).
      return message.toLowerCase().includes(label.toLowerCase()) ? message : `${label}: ${message}`;
    })
    .join('; ');
}

function duplicateNote(
  row: FiasLeadsImportPreviewRow,
  strategy: FiasLeadsImportDedupStrategy,
): string {
  const dup = row.duplicateOf;
  if (!dup) return '';
  const action = strategy === 'skip' ? 'will be skipped' : 'will be imported anyway';
  if (dup.kind === 'existing') {
    return `Matches existing ${dup.label ?? dup.id ?? 'record'} — ${action}`;
  }
  const where = dup.rowIndex !== undefined ? `row ${dup.rowIndex + 1}` : 'an earlier row';
  return `Duplicate of ${where} in this file — ${action}`;
}

export function PreviewStep({
  preview,
  fields,
  mapping,
  dedupStrategy,
  onDedupStrategyChange,
  nameFallback,
  onNameFallbackChange,
  csvRows,
  csvHeaders,
  effectiveRows,
  droppedRows,
  onApplyRowEdit,
  onDropRow,
  onRestoreRow,
}: Props) {
  const [filter, setFilter] = useState<RowFilter>('all');
  const [inspectedIndex, setInspectedIndex] = useState<number | null>(null);

  // Table columns: every mapped field, plus any field the server
  // populated on its own (e.g. "Other emails" spilled over from a
  // multi-address Email cell) — derived data must be visible too.
  const columns = useMemo(
    () =>
      fields.filter((f) => {
        if ((mapping[f.field] ?? { kind: 'none' }).kind !== 'none') return true;
        return preview.rows.some((r) => {
          const v = r.fields[f.field]?.value;
          return v !== null && v !== undefined && v !== '';
        });
      }),
    [fields, mapping, preview.rows],
  );
  const labelByField = useMemo(() => new Map(fields.map((f) => [f.field, f.label])), [fields]);

  type DisplayRow =
    | { kind: 'preview'; index: number; row: FiasLeadsImportPreviewRow }
    | { kind: 'dropped'; index: number };

  const filteredRows = useMemo<DisplayRow[]>(() => {
    let rows = preview.rows;
    if (filter === 'duplicate') rows = rows.filter((r) => r.duplicateOf);
    else if (filter !== 'all') rows = rows.filter((r) => r.rowStatus === filter);
    const display: DisplayRow[] = rows.map((row) => ({ kind: 'preview', index: row.index, row }));
    if (filter === 'all') {
      for (const index of droppedRows) display.push({ kind: 'dropped', index });
      display.sort((a, b) => a.index - b.index);
    }
    return display;
  }, [preview.rows, filter, droppedRows]);

  const { summary } = preview;
  const duplicates = summary.duplicatesExisting + summary.duplicatesInFile;

  const filterBadge = (key: RowFilter, label: string, count: number, className: string) => (
    <button
      type="button"
      onClick={() => setFilter(filter === key ? 'all' : key)}
      aria-pressed={filter === key}
      className={cn('rounded-full', filter === key && 'ring-2 ring-ring')}
    >
      <Badge variant="outline" className={className}>
        {count} {label}
      </Badge>
    </button>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {filterBadge(
          'ok',
          'ready',
          summary.ok,
          'border-emerald-500/50 text-emerald-600 dark:text-emerald-400',
        )}
        {filterBadge(
          'warning',
          'with fixes',
          summary.warnings,
          'border-amber-500/50 text-amber-600 dark:text-amber-400',
        )}
        {filterBadge('error', 'blocked', summary.errors, 'border-destructive/50 text-destructive')}
        {filterBadge(
          'duplicate',
          'duplicates',
          duplicates,
          'border-sky-500/50 text-sky-600 dark:text-sky-400',
        )}
        <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {preview.entityType === 'contact' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Missing first name</span>
              <Select
                value={nameFallback}
                onValueChange={(v) => onNameFallbackChange(v as FiasLeadsImportNameFallback)}
              >
                <SelectTrigger className="h-8 w-48" aria-label="Missing first name handling">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Block the row</SelectItem>
                  <SelectItem value="email">Use the email as name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Duplicates</span>
            <Select
              value={dedupStrategy}
              onValueChange={(v) => onDedupStrategyChange(v as FiasLeadsImportDedupStrategy)}
            >
              <SelectTrigger className="h-8 w-44" aria-label="Duplicate handling">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Skip duplicates</SelectItem>
                <SelectItem value="create_anyway">Import anyway</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        This is your data after cleanup — exactly what will be saved. Hover a highlighted cell to
        see what changed, or click any row to see its original data, edit it, or drop it.
      </p>

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">Row</TableHead>
              <TableHead className="w-24">Status</TableHead>
              {columns.map((c) => (
                <TableHead key={c.field}>{c.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.slice(0, PREVIEW_ROW_RENDER_CAP).map((display) => {
              if (display.kind === 'dropped') {
                return (
                  <TableRow
                    key={display.index}
                    className="cursor-pointer opacity-50"
                    onClick={() => setInspectedIndex(display.index)}
                  >
                    <TableCell className="text-xs text-muted-foreground">
                      {display.index + 1}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs text-muted-foreground">
                        Dropped
                      </Badge>
                      <p className="mt-1 text-[11px] text-muted-foreground">Won't be imported</p>
                    </TableCell>
                    {columns.map((c, i) => (
                      <TableCell key={c.field} className="text-sm line-through">
                        {i === columns.length - 1 ? (
                          <span className="flex items-center justify-between gap-2 no-underline">
                            <span>{effectiveRows[display.index]?.[c.field] || '—'}</span>
                            <button
                              type="button"
                              className="text-xs text-primary underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRestoreRow(display.index);
                              }}
                            >
                              Restore
                            </button>
                          </span>
                        ) : (
                          effectiveRows[display.index]?.[c.field] || '—'
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              }
              const row = display.row;
              const note = duplicateNote(row, dedupStrategy);
              return (
                <TableRow
                  key={row.index}
                  className="cursor-pointer"
                  onClick={() => setInspectedIndex(row.index)}
                >
                  <TableCell className="text-xs text-muted-foreground">{row.index + 1}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs',
                        row.rowStatus === 'ok' &&
                          'border-emerald-500/50 text-emerald-600 dark:text-emerald-400',
                        row.rowStatus === 'warning' &&
                          'border-amber-500/50 text-amber-600 dark:text-amber-400',
                        row.rowStatus === 'error' && 'border-destructive/50 text-destructive',
                      )}
                    >
                      {row.rowStatus === 'ok'
                        ? 'Ready'
                        : row.rowStatus === 'warning'
                          ? 'Fixed'
                          : 'Blocked'}
                    </Badge>
                    {row.rowStatus === 'error' && (
                      <p className="mt-1 max-w-48 text-[11px] text-destructive">
                        {blockedReasons(row, labelByField)}
                      </p>
                    )}
                    {note && (
                      <p className="mt-1 text-[11px] text-sky-600 dark:text-sky-400">{note}</p>
                    )}
                    {row.resolution && (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Stage: {row.resolution.stage.name}
                        {row.resolution.stage.matchedBy === 'default' ? ' (default)' : ''}
                      </p>
                    )}
                  </TableCell>
                  {columns.map((c) => {
                    const cell = row.fields[c.field];
                    const value = cell?.value;
                    return (
                      <TableCell
                        key={c.field}
                        title={cell?.issue?.message}
                        className={cn(
                          'text-sm',
                          cell?.issue?.level === 'warning' && 'bg-amber-500/10',
                          cell?.issue?.level === 'error' && 'bg-destructive/10',
                        )}
                      >
                        {value === null || value === undefined || value === '' ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          String(value)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {filteredRows.length > PREVIEW_ROW_RENDER_CAP && (
        <p className="text-xs text-muted-foreground">
          Showing the first {PREVIEW_ROW_RENDER_CAP} of {filteredRows.length.toLocaleString()} rows.
        </p>
      )}

      {summary.errors > 0 ? (
        <p className="text-sm text-destructive">
          {summary.errors} row{summary.errors === 1 ? ' is' : 's are'} blocked, so the import can't
          start yet. Click a blocked row to fix it or drop it — no need to edit your CSV.
        </p>
      ) : summary.warnings > 0 ? (
        <p className="text-sm text-muted-foreground">
          Rows marked "Fixed" had small cleanups applied (shown above). They import fine.
        </p>
      ) : null}

      {inspectedIndex !== null && (
        <RowDetailDialog
          open
          rowIndex={inspectedIndex}
          rawRow={csvRows[inspectedIndex] ?? {}}
          csvHeaders={csvHeaders}
          fields={fields}
          currentValues={effectiveRows[inspectedIndex] ?? {}}
          isDropped={droppedRows.includes(inspectedIndex)}
          onApply={(values) => onApplyRowEdit(inspectedIndex, values)}
          onDrop={() => onDropRow(inspectedIndex)}
          onRestore={() => onRestoreRow(inspectedIndex)}
          onClose={() => setInspectedIndex(null)}
        />
      )}
    </div>
  );
}
